import { Flex, TextHeadingOne } from '@cko/primitives';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useFeatureFlags } from '@cko/dashboard-shared';
import { PrimaryHeader } from '../../../../../config/shared/types';
import { FeatureFlagId } from '../../../../../config/shared/enums/featureFlagIds';
import { BreadCrumb, BreadcrumbsItem } from '../BreadCrumb';
import { Description } from '../Description';
import { AvailableMenuItem } from '../../../../../providers/NavigationProvider/types';
import { TabbedMenu } from '../TabbedMenu';

const Container = styled(Flex)`
  flex-direction: column;
  gap: var(--layout-soybean);
`;

const OldContainer = styled(Flex)`
  flex-direction: column;
  gap: var(--layout-soybean);
  margin-bottom: var(--layout-shoe);
`;

export type MainHeaderProps = {
  config: PrimaryHeader;
  breadCrumbItems: Array<BreadcrumbsItem>;
  menuItems?: Array<AvailableMenuItem>;
  dynamicDescription?: string;
};

// used to ensure header text does not jump about when breadcrumbs are not visible
const BreadCrumbSpacer = styled.div`
  height: 24px;
`;

export const MainHeader = ({
  config,
  breadCrumbItems,
  menuItems,
  dynamicDescription,
}: MainHeaderProps) => {
  const {
    title,
    description,
    external_link: externalLink,
    dynamic_description: dynamicDescriptionEnabled,
  } = config;
  const { getFeatureFlag } = useFeatureFlags();
  const isNewHeaderEnabled = getFeatureFlag(FeatureFlagId.NEW_HEADINGS);
  const shouldHideBreadCrumbs = useMemo(
    () => Boolean(menuItems) || breadCrumbItems.length === 0,
    [menuItems, breadCrumbItems],
  );

  if (!isNewHeaderEnabled) {
    return <OldContainer>{menuItems && <TabbedMenu menuItems={menuItems} />}</OldContainer>;
  }

  // we only show breadcrumbs if no menu should be shown on the page. this is due to menu it self being a navigation aide.
  // breadcrumbs incorrectly show the current page as available when selecting different menu items. we still want the component on the screen
  // to prevent header shifting up so just send an empty array if not supposed to be seen on this page
  return (
    <Container>
      {shouldHideBreadCrumbs ? <BreadCrumbSpacer /> : <BreadCrumb items={breadCrumbItems} />}
      <TextHeadingOne>{title}</TextHeadingOne>
      <Description
        descriptionText={description}
        documentation={externalLink}
        dynamicDescriptionEnabled={dynamicDescriptionEnabled}
        dynamicDescription={dynamicDescription}
      />
      {menuItems && <TabbedMenu menuItems={menuItems} />}
    </Container>
  );
};
